import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import bgVideo from "../assets/bg.mp4";
import { printReceipt } from "../utils/receipt";

function Toast({ show, message, type, onClose }) {
  if (!show) return null;

  return (
    <div
      className={`fixed right-4 top-4 z-50 rounded-lg px-5 py-3 text-sm font-semibold text-white shadow-2xl ${
        type === "success" ? "bg-teal-600" : "bg-red-600"
      }`}
    >
      <div className="flex items-center gap-3">
        <span>{message}</span>
        <button onClick={onClose} className="text-white/80 hover:text-white">
          X
        </button>
      </div>
    </div>
  );
}

function ImageModal({ show, onClose, imageSrc }) {
  if (!show) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
      <div className="relative flex max-h-[88vh] w-full max-w-5xl flex-col rounded-lg border border-white/10 bg-slate-950 p-4 shadow-2xl">
        <div className="mb-3 flex items-center justify-between border-b border-white/10 pb-3">
          <h3 className="text-lg font-bold text-white">Payment Proof</h3>
          <button
            onClick={onClose}
            className="rounded-lg border border-white/15 px-3 py-1 text-sm font-semibold text-slate-200 transition hover:bg-white/10"
          >
            X
          </button>
        </div>
        <img
          src={`data:image/*;base64,${imageSrc}`}
          alt="Payment Proof"
          className="min-h-0 w-full flex-1 rounded object-contain"
        />
      </div>
    </div>
  );
}

function AppealModal({
  payment,
  message,
  setMessage,
  proof,
  proofPreview,
  onProofChange,
  onClose,
  onSubmit,
}) {
  if (!payment) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4">
      <div className="w-full max-w-lg rounded-lg border border-white/10 bg-slate-950 p-6 text-white shadow-2xl">
        <div className="mb-5 border-b border-white/10 pb-4">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-teal-300">
            Payment Response
          </p>
          <h3 className="mt-2 text-2xl font-bold">
            {payment.property?.property_name || "Linked rental"}
          </h3>
          <p className="mt-1 text-sm text-slate-400">
            Send a note for admin review.
          </p>
        </div>

        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={4}
          className="w-full resize-none rounded-lg border border-white/10 bg-white px-3 py-3 text-sm text-slate-900 outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30"
          placeholder="Explain your correction or clarification"
        />

        <div className="mt-4 rounded-lg border border-dashed border-white/15 bg-white/5 p-4">
          <p className="mb-2 text-sm font-semibold text-slate-200">
            Additional Proof (optional)
          </p>
          <input
            type="file"
            accept="image/*"
            onChange={onProofChange}
            className="w-full text-sm text-slate-300 file:mr-4 file:rounded-lg file:border-0 file:bg-teal-600 file:px-4 file:py-2 file:font-semibold file:text-white hover:file:bg-teal-500"
          />
          {proof && (
            <div className="mt-3 rounded-lg border border-white/10 bg-slate-950/80 p-3">
              <p className="text-sm font-semibold text-white">{proof.name}</p>
              {proofPreview && (
                <img
                  src={proofPreview}
                  alt="Appeal proof preview"
                  className="mt-3 max-h-48 w-full rounded object-contain"
                />
              )}
            </div>
          )}
        </div>

        <div className="mt-5 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="rounded-lg border border-white/15 px-5 py-2 font-semibold text-slate-200 transition hover:bg-white/10"
          >
            Cancel
          </button>
          <button
            onClick={onSubmit}
            className="rounded-lg bg-teal-600 px-5 py-2 font-semibold text-white transition hover:bg-teal-500"
          >
            Submit Response
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PaymentHistory({ refreshTrigger }) {
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "success",
  });
  const [imageModal, setImageModal] = useState({ show: false, data: null });
  const [appealPayment, setAppealPayment] = useState(null);
  const [appealMessage, setAppealMessage] = useState("");
  const [appealProof, setAppealProof] = useState(null);
  const [appealProofPreview, setAppealProofPreview] = useState(null);

  const showToast = useCallback((message, type = "error") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type }), 3000);
  }, []);

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const [paymentsRes, balancesRes] = await Promise.all([
        api.get("/payments/my", {
          headers: { Authorization: `Bearer ${token}` },
        }),
        api.get("/payments/summary", {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setPayments(Array.isArray(paymentsRes.data) ? paymentsRes.data : []);
      setBalances(Array.isArray(balancesRes.data) ? balancesRes.data : []);
    } catch (err) {
      console.error(err);
      showToast("Failed to fetch payments", "error");
      setPayments([]);
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments, refreshTrigger]);

  useEffect(() => {
    if (!appealProof || !appealProof.type.startsWith("image/")) {
      setAppealProofPreview(null);
      return;
    }

    const previewUrl = URL.createObjectURL(appealProof);
    setAppealProofPreview(previewUrl);

    return () => URL.revokeObjectURL(previewUrl);
  }, [appealProof]);

  const formatAmount = (amount) =>
    `PHP ${Number(amount || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const statusClass = (status) => {
    if (status === "approved") return "border-teal-300/40 bg-teal-500/15 text-teal-200";
    if (status === "rejected") return "border-red-300/40 bg-red-500/15 text-red-200";
    if (status === "needs_clarification") return "border-amber-300/40 bg-amber-500/15 text-amber-100";
    return "border-amber-300/40 bg-amber-500/15 text-amber-200";
  };

  const statusLabel = (status) =>
    status === "needs_clarification"
      ? "Needs Clarification"
      : status;

  const formatDateTime = (value) => {
    if (!value) return "-";
    return new Date(value).toLocaleString();
  };

  const handleAppealSubmit = async () => {
    if (!appealPayment) return;

    if (!appealMessage.trim()) {
      showToast("Please enter your response", "error");
      return;
    }

    try {
      const payload = new FormData();
      payload.append("appeal_message", appealMessage);
      if (appealProof) payload.append("appeal_proof", appealProof);

      payload.append("_method", "PUT");
      await api.post(`/payments/${appealPayment.id}/appeal`, payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAppealPayment(null);
      setAppealMessage("");
      setAppealProof(null);
      fetchPayments();
      showToast("Response submitted for admin review", "success");
    } catch (err) {
      console.error(err);
      showToast(err.response?.data?.message || "Failed to submit response", "error");
    }
  };

  const handleReceipt = async (paymentId) => {
    try {
      const res = await api.get(`/payments/${paymentId}/receipt`);
      printReceipt(res.data);
    } catch (err) {
      console.error(err);
      showToast(err.response?.data?.message || "Receipt is available for approved payments only", "error");
    }
  };

  const filteredPayments = useMemo(() => {
    const query = searchTerm;

    return payments.filter((payment) => {
      const matchesStatus =
        statusFilter === "all" || payment.status === statusFilter;
      const matchesSearch = !query || String(payment.property_id || payment.property?.id) === query;
      return matchesStatus && matchesSearch;
    });
  }, [payments, searchTerm, statusFilter]);

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-950 font-sans">
      <div className="absolute inset-0">
        <video data-workspace-background="true" aria-hidden="true"
          src={bgVideo}
          autoPlay
          loop
          muted
          playsInline
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-slate-950/85" />
      </div>

      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ show: false, message: "", type: toast.type })}
      />

      <ImageModal
        show={imageModal.show}
        imageSrc={imageModal.data}
        onClose={() => setImageModal({ show: false, data: null })}
      />

      <AppealModal
        payment={appealPayment}
        message={appealMessage}
        setMessage={setAppealMessage}
        proof={appealProof}
        proofPreview={appealProofPreview}
        onProofChange={(e) => setAppealProof(e.target.files[0] || null)}
        onClose={() => {
          setAppealPayment(null);
          setAppealMessage("");
          setAppealProof(null);
        }}
        onSubmit={handleAppealSubmit}
      />

      <div className="workspace-page-body relative z-10 min-h-screen px-4 py-8 text-white sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl">
          <div className="mb-6 flex flex-col gap-4 border-b border-white/10 pb-5 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.35em] text-teal-300">
                Tenant Payments
              </p>
              <h1 className="mt-2 text-3xl font-bold tracking-wide md:text-4xl">
                Payment History
              </h1>
              <p className="mt-2 max-w-2xl text-sm text-slate-300 md:text-base">
                Track submitted payments, review status updates, and open proof
                records.
              </p>
            </div>

            <button
              onClick={() => navigate("/payments")}
              className="w-fit rounded-lg bg-teal-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-teal-500"
            >
              Make a Payment
            </button>
          </div>

          {balances.length > 0 && (
            <div className="mb-5 grid gap-3 md:grid-cols-2">
              {balances.map((item) => (
                <div
                  key={item.property_id}
                  className="rounded-lg border border-white/10 bg-slate-950/88 p-4 shadow-xl"
                >
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.22em] text-teal-300">
                        Linked Rental
                      </p>
                      <h3 className="mt-2 text-lg font-bold text-white">
                        {item.property_name}
                      </h3>
                      <p className="mt-1 text-sm text-slate-400">
                        {item.location}
                      </p>
                    </div>
                    <div className="rounded-lg border border-teal-300/30 bg-teal-500/10 px-4 py-3 text-right">
                      <p className="text-xs uppercase tracking-[0.18em] text-slate-400">
                        Balance
                      </p>
                      <p className="mt-1 text-xl font-bold text-teal-200">
                        {formatAmount(item.remaining_balance)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mb-5 flex flex-col gap-3 rounded-lg border border-white/10 bg-slate-950/90 p-4 shadow-xl lg:flex-row lg:items-center lg:justify-between">
            <select aria-label="Filter by rental" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="rounded-lg bg-white px-3 py-3 text-sm text-slate-900">
              <option value="">All rentals</option>
              {balances.map((rental) => <option key={rental.property_id} value={rental.property_id}>{rental.property_name}</option>)}
            </select>

            <div className="flex gap-2 overflow-x-auto pb-1 whitespace-nowrap">
              {["all", "pending", "needs_clarification", "approved", "rejected"].map((status) => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={`shrink-0 rounded-lg px-4 py-2 text-sm font-semibold capitalize transition ${
                    statusFilter === status
                      ? "bg-teal-600 text-white"
                      : "border border-white/10 bg-white/5 text-slate-200 hover:bg-white/10"
                  }`}
                >
                  {statusLabel(status)}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="rounded-lg border border-white/10 bg-slate-950/90 py-10 text-center text-slate-300">
              Loading payments...
            </div>
          ) : filteredPayments.length === 0 ? (
            <div className="rounded-lg border border-dashed border-white/15 bg-slate-950/70 py-10 text-center text-slate-300">
              No payment records found.
            </div>
          ) : (
            <div className="grid gap-4 lg:grid-cols-2">
              {filteredPayments.map((p) => (
                <div
                  key={p.id}
                  className="rounded-lg border border-white/10 bg-slate-950/90 p-4 shadow-xl"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="text-lg font-bold text-white">
                        {formatAmount(p.amount)}
                      </h3>
                      <p className="mt-1 text-sm font-semibold text-slate-100">
                        {p.property?.property_name || "Linked rental"}
                      </p>
                      <p className="mt-1 text-sm text-slate-300">
                        {p.method} - {p.month_paid_for || "-"}
                      </p>
                    </div>

                    <span
                      className={`shrink-0 rounded-full border px-3 py-1 text-xs font-semibold ${statusClass(
                        p.status
                      )}`}
                    >
                      {statusLabel(p.status)}
                    </span>
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-3 rounded-lg border border-white/10 bg-white/5 p-3 text-sm">
                    <div>
                      <p className="text-xs uppercase tracking-[0.18em] text-slate-500">
                        Type
                      </p>
                      <p className="mt-1 font-semibold capitalize text-white">
                        {p.payment_type || "partial"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-[0.18em] text-slate-500">
                        Balance
                      </p>
                      <p className="mt-1 font-semibold text-teal-200">
                        {formatAmount(p.balance_after)}
                      </p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-xs uppercase tracking-[0.18em] text-slate-500">
                        Timeline
                      </p>
                      <div className="mt-1 space-y-1 text-xs text-slate-400">
                        <p>Submitted: {formatDateTime(p.created_at)}</p>
                        {p.approved_at && <p>Approved: {formatDateTime(p.approved_at)}</p>}
                        {p.rejected_at && <p>Rejected: {formatDateTime(p.rejected_at)}</p>}
                        {p.clarification_at && <p>Clarified: {formatDateTime(p.clarification_at)}</p>}
                        {p.appealed_at && <p>Appealed: {formatDateTime(p.appealed_at)}</p>}
                      </div>
                    </div>
                  </div>

                  {(p.rejected_reason || p.clarification_reason || p.appeal_message || p.notes) && (
                    <p
                      className={`mt-4 rounded-lg p-3 text-sm ${
                        p.rejected_reason
                          ? "bg-red-500/10 text-red-100"
                          : p.clarification_reason
                          ? "bg-amber-500/10 text-amber-100"
                          : "bg-white/5 text-slate-300"
                      }`}
                    >
                      {p.appeal_message ||
                        p.clarification_reason ||
                        p.rejected_reason ||
                        p.notes}
                    </p>
                  )}

                <div className="mt-4 flex flex-wrap gap-2">
                  {p.proof_path && (
                    <button
                        onClick={() =>
                          setImageModal({ show: true, data: p.proof_path })
                        }
                        className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm font-semibold text-slate-100"
                    >
                      View Proof
                    </button>
                  )}
                  {p.proof_path && (
                    <span
                      className={`inline-flex rounded-full border px-2 py-1 text-xs font-semibold ${
                        p.proof_integrity?.status === "verified"
                          ? "border-teal-300/40 bg-teal-500/15 text-teal-200"
                          : p.proof_integrity?.status === "tampered"
                          ? "border-red-300/40 bg-red-500/15 text-red-200"
                          : "border-amber-300/40 bg-amber-500/15 text-amber-200"
                      }`}
                    >
                      Proof {p.proof_integrity?.status || "unknown"}
                    </span>
                  )}

                  {p.appeal_proof_path && (
                    <button
                        onClick={() =>
                          setImageModal({ show: true, data: p.appeal_proof_path })
                        }
                        className="rounded-lg border border-teal-300/40 bg-teal-500/10 px-3 py-2 text-sm font-semibold text-teal-100"
                    >
                      View Appeal Proof
                    </button>
                  )}
                  {p.appeal_proof_path && (
                    <span
                      className={`inline-flex rounded-full border px-2 py-1 text-xs font-semibold ${
                        p.appeal_proof_integrity?.status === "verified"
                          ? "border-teal-300/40 bg-teal-500/15 text-teal-200"
                          : p.appeal_proof_integrity?.status === "tampered"
                          ? "border-red-300/40 bg-red-500/15 text-red-200"
                          : "border-amber-300/40 bg-amber-500/15 text-amber-200"
                      }`}
                    >
                      Appeal {p.appeal_proof_integrity?.status || "unknown"}
                    </span>
                  )}

                  {["rejected", "needs_clarification"].includes(p.status) && (
                    <button
                        onClick={() => {
                          setAppealPayment(p);
                          setAppealMessage("");
                          setAppealProof(null);
                        }}
                        className="ml-auto rounded-lg bg-teal-600 px-3 py-2 text-sm font-semibold text-white"
                      >
                        Respond
                      </button>
                    )}
                  {p.status === "approved" && (
                    <button
                      onClick={() => handleReceipt(p.id)}
                      className="ml-auto rounded-lg border border-white/15 px-3 py-2 text-sm font-semibold text-slate-100"
                    >
                      Receipt PDF
                    </button>
                  )}
                </div>
              </div>
            ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
